
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

// =====================================
// FRONTSPACE SEO CONFIGURATION
// =====================================

const SITE_URL = "https://frontspace.io";
const SITE_NAME = "FrontSpace";

// Set this to a real, publicly accessible image URL
// after uploading your social sharing image.
// Recommended image size: 1200 x 630 pixels.
const OG_IMAGE = null;

// =====================================
// PAGE SEO DATA
// =====================================

const META = {
  "/": {
    title: "Cybersecurity, AI & Software Solutions | FrontSpace",
    description:
      "FrontSpace provides cybersecurity services, 24/7 SOC monitoring, custom software development, AI solutions, business automation, and practical cyber training.",
    type: "WebPage",
  },

  "/aboutus": {
    title: "About FrontSpace | Cybersecurity & Technology Company",
    description:
      "Discover FrontSpace, a technology company developing custom software, AI solutions, business automation, SOC services, and practical cybersecurity training.",
    type: "AboutPage",
  },

  "/services": {
    title: "Cybersecurity, SOC & Digital Solutions | FrontSpace",
    description:
      "Explore FrontSpace services, including 24/7 SOC monitoring, custom software, AI platforms, business automation, penetration testing, and cybersecurity training.",
    type: "CollectionPage",
  },

  "/news": {
    title: "Cybersecurity & Technology News | FrontSpace",
    description:
      "Explore cybersecurity news, artificial intelligence developments, emerging technology, digital trends, and practical insights from the FrontSpace team.",
    type: "CollectionPage",
  },

  "/contact": {
    title: "Contact FrontSpace | Cybersecurity & Software Solutions",
    description:
      "Contact FrontSpace about cybersecurity and SOC services, software development, AI solutions, business automation, technology partnerships, or cyber training.",
    type: "ContactPage",
  },

  // Specialist service pages.
  // These URLs should only be indexed if corresponding
  // React routes and unique page content exist.

  "/services/soc": {
    title: "24/7 SOC & Managed Cybersecurity Services | FrontSpace",
    description:
      "Protect your organization with FrontSpace SOC services, 24/7 security monitoring, threat detection, incident response, and managed cybersecurity expertise.",
    type: "Service",
  },

  "/services/cyber-academy": {
    title: "Cybersecurity Training & Practical Courses | FrontSpace",
    description:
      "Build cybersecurity skills through hands-on training, realistic labs, technical tools, and career-focused learning programs at FrontSpace Cyber Academy.",
    type: "Service",
  },

  "/services/digital-solutions": {
    title: "Custom Software Development & AI Solutions | FrontSpace",
    description:
      "Build custom applications, web platforms, CRM and ERP systems, AI-powered tools, and automated digital workflows tailored to your business with FrontSpace.",
    type: "Service",
  },

  "/terms": {
    title: "Terms & Conditions | FrontSpace",
    description:
      "Read the terms and conditions governing the use of the FrontSpace website and its services.",
    type: "WebPage",
  },

  "/cookies": {
    title: "Cookie Policy | FrontSpace",
    description:
      "Learn how FrontSpace uses cookies and related technologies on frontspace.io.",
    type: "WebPage",
  },
};

// =====================================
// HELPERS
// =====================================

function updateMeta(name, content, attribute = "name") {
  let element = document.head.querySelector(
    `meta[${attribute}="${name}"]`
  );

  if (!content) {
    if (element) {
      element.remove();
    }
    return;
  }

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function updateCanonical(url) {
  const links = document.head.querySelectorAll(
    'link[rel="canonical"]'
  );

  let canonical = links[0];

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", url);

  // Avoid conflicting duplicate canonical tags.
  links.forEach((link, index) => {
    if (index > 0) {
      link.remove();
    }
  });
}

function updateJSONLD(data) {
  const id = "frontspace-seo-jsonld";

  let script = document.getElementById(id);

  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

function removeJSONLD() {
  const script = document.getElementById(
    "frontspace-seo-jsonld"
  );

  if (script) {
    script.remove();
  }
}

function normalizePath(path) {
  if (!path || path === "/") {
    return "/";
  }

  return path.replace(/\/+$/, "");
}

// =====================================
// MAIN SEO COMPONENT
// =====================================

export default function SEOHead() {
  const location = useLocation();
  const { language } = useLanguage();

  const pathname = normalizePath(location.pathname);
  const isNewsArticle = pathname.startsWith("/news/");
  const meta = META[pathname] || null;

  useEffect(() => {
    // Individual news pages should manage their
    // own article-specific SEO metadata.
    if (isNewsArticle) {
      removeJSONLD();
      return;
    }

    const page = meta || {
      title: "FrontSpace | Technology Solutions",
      description:
        "Explore FrontSpace software development, artificial intelligence, business automation, and cybersecurity solutions.",
      type: "WebPage",
    };

    const canonicalURL =
      SITE_URL + (pathname === "/" ? "/" : pathname);

    // =====================================
    // DOCUMENT TITLE
    // =====================================

    document.title = page.title;

    // =====================================
    // STANDARD META
    // =====================================

    updateMeta("description", page.description);
    updateMeta("keywords", null);
    updateMeta("robots", "index, follow");

    // =====================================
    // OPEN GRAPH
    // =====================================

    updateMeta("og:title", page.title, "property");
    updateMeta(
      "og:description",
      page.description,
      "property"
    );
    updateMeta("og:type", "website", "property");
    updateMeta("og:url", canonicalURL, "property");
    updateMeta("og:site_name", SITE_NAME, "property");

    const localeMap = {
      en: "en_US",
      al: "sq_AL",
      it: "it_IT",
      es: "es_ES",
    };

    updateMeta(
      "og:locale",
      localeMap[language] || "en_US",
      "property"
    );

    // =====================================
    // SOCIAL SHARING IMAGE
    // =====================================

    if (OG_IMAGE) {
      updateMeta("og:image", OG_IMAGE, "property");
    } else {
      updateMeta("og:image", null, "property");
    }

    // =====================================
    // TWITTER / X
    // =====================================

    updateMeta(
      "twitter:card",
      OG_IMAGE ? "summary_large_image" : "summary"
    );

    updateMeta("twitter:title", page.title);
    updateMeta("twitter:description", page.description);
    updateMeta("twitter:image", OG_IMAGE);

    // =====================================
    // CANONICAL URL
    // =====================================

    updateCanonical(canonicalURL);

    // =====================================
    // STRUCTURED DATA
    // =====================================

    const organization = {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: SITE_NAME,
      url: `${SITE_URL}/`,
      description:
        "Technology company specializing in cybersecurity, software development, artificial intelligence, business automation, and cybersecurity education.",
      email: "office@frontspace.io",
      telephone: "+355682008275",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Kavaja Street, Deliorgj",
        addressLocality: "Tirana",
        postalCode: "1001",
        addressCountry: "AL",
      },
      sameAs: [
        "https://www.instagram.com/frontspace__",
        "https://www.linkedin.com/company/140233961",
      ],
    };

    const website = {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: SITE_NAME,
      url: `${SITE_URL}/`,
      publisher: {
        "@id": `${SITE_URL}/#organization`,
      },
    };

    const webpage = {
      "@type":
        page.type === "Service"
          ? "WebPage"
          : page.type,
      "@id": `${canonicalURL}#webpage`,
      url: canonicalURL,
      name: page.title,
      description: page.description,
      isPartOf: {
        "@id": `${SITE_URL}/#website`,
      },
      about: {
        "@id": `${SITE_URL}/#organization`,
      },
      inLanguage:
        language === "al" ? "sq" : language,
    };

    const graph = [organization, website, webpage];

    // =====================================
    // SERVICE STRUCTURED DATA
    // =====================================

    if (page.type === "Service") {
      graph.push({
        "@type": "Service",
        "@id": `${canonicalURL}#service`,
        name: page.title.split("|")[0].trim(),
        description: page.description,
        url: canonicalURL,
        provider: {
          "@id": `${SITE_URL}/#organization`,
        },
        areaServed: {
          "@type": "Place",
          name: "Worldwide",
        },
      });
    }

    updateJSONLD({
      "@context": "https://schema.org",
      "@graph": graph,
    });

    return () => {
      removeJSONLD();
    };
  }, [pathname, meta, language, isNewsArticle]);

  return null;
}
